import { Button, ProgressBar } from '@fold-and-flow/ui'
import type { MuxPlayerRefAttributes } from '@mux/mux-player-react'
import MuxPlayer from '@mux/mux-player-react'
import { useNavigate, useParams } from '@tanstack/react-router'
import { AnimatePresence, motion } from 'framer-motion'
import { useCallback, useEffect, useRef, useState } from 'react'
import { trpc } from '#lib/trpc'

export function SessionPlayerPage() {
  const { sessionId } = useParams({ from: '/_app/session/$sessionId' })
  const navigate = useNavigate()

  const { data: currentSession } = trpc.session.byId.useQuery({ id: sessionId })

  const { data: flow } = trpc.flow.byId.useQuery(
    { id: currentSession?.flowId ?? '' },
    { enabled: !!currentSession?.flowId }
  )

  const completeSession = trpc.session.complete.useMutation()

  const [currentItemIndex, setCurrentItemIndex] = useState(0)
  const [elapsed, setElapsed] = useState(0) // seconds
  const [isPlaying, setIsPlaying] = useState(true)
  const playerRef = useRef<MuxPlayerRefAttributes | null>(null)

  const items = flow?.items ?? []
  const currentItem = items[currentItemIndex]
  // TODO: `durationOverrideMin` is really the planned phase duration; rename it when we do the API/DB cleanup.
  const itemDurationSec = (currentItem?.durationOverrideMin ?? 5) * 60
  const { data: playbackId, isLoading: playbackLoading } = trpc.clips.getPlaybackId.useQuery(
    { id: currentItem?.clipId ?? '' },
    { enabled: !!currentItem?.clipId }
  )

  const advanceOrComplete = useCallback(async () => {
    if (currentItemIndex < items.length - 1) {
      setCurrentItemIndex((i) => i + 1)
      setElapsed(0)
    } else {
      await completeSession.mutateAsync({ sessionId, completionRate: 1 })
      await navigate({ to: '/journal/$sessionId', params: { sessionId } })
    }
  }, [currentItemIndex, items.length, completeSession, sessionId, navigate])

  useEffect(() => {
    setElapsed(0)
    setIsPlaying(true)
  }, [currentItemIndex])

  function handleTimeUpdate() {
    const nextElapsed = Math.floor(playerRef.current?.currentTime ?? 0)
    setElapsed(nextElapsed)
  }

  function handlePause() {
    setIsPlaying(false)
  }

  function handlePlaying() {
    setIsPlaying(true)
  }

  function handleEnded() {
    void advanceOrComplete()
  }

  function handleTogglePlayback() {
    setIsPlaying((isCurrentPlaying) => !isCurrentPlaying)
  }

  async function handleEarlyExit() {
    const completionRate =
      items.length > 0 ? (currentItemIndex + elapsed / itemDurationSec) / items.length : 0
    await completeSession.mutateAsync({ sessionId, completionRate: Math.min(1, completionRate) })
    await navigate({ to: '/journal/$sessionId', params: { sessionId } })
  }

  if (!flow || items.length === 0) {
    return (
      <div className="min-h-screen bg-warm-gray-900 flex items-center justify-center text-white">
        <p>Loading your practice…</p>
      </div>
    )
  }

  const mins = Math.floor(elapsed / 60)
  const secs = elapsed % 60
  const totalMins = Math.floor(itemDurationSec / 60)
  const totalSecs = itemDurationSec % 60

  return (
    <div className="min-h-screen bg-warm-gray-900 flex flex-col">
      {/* Progress */}
      <div className="px-6 pt-6">
        <ProgressBar
          value={(currentItemIndex + elapsed / itemDurationSec) / items.length}
          label="Session progress"
          className="opacity-60"
        />
        <div className="flex justify-between items-center mt-2">
          <span className="text-xs text-warm-gray-400">
            Phase {currentItemIndex + 1} of {items.length}
          </span>
          <button
            type="button"
            onClick={handleEarlyExit}
            className="text-xs text-warm-gray-400 hover:text-white transition-colors"
          >
            End early
          </button>
        </div>
      </div>

      {/* Video area */}
      <div className="flex-1 flex items-center justify-center px-6 py-8">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentItemIndex}
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.97 }}
            transition={{ duration: 0.4 }}
            className="w-full max-w-2xl aspect-video rounded-2xl bg-warm-gray-800 overflow-hidden"
          >
            {playbackLoading ? (
              <div className="w-full h-full flex items-center justify-center text-warm-gray-400">
                <span className="text-sm">Loading video…</span>
              </div>
            ) : playbackId ? (
              <MuxPlayer
                ref={playerRef}
                playbackId={playbackId}
                className="w-full h-full"
                style={{ aspectRatio: '16/9' }}
                autoPlay
                paused={!isPlaying}
                playsInline
                onTimeUpdate={handleTimeUpdate}
                onPause={handlePause}
                onPlaying={handlePlaying}
                onEnded={handleEnded}
              />
            ) : (
              <div className="w-full h-full flex flex-col items-center justify-center gap-2 text-warm-gray-400">
                <span className="text-base font-medium text-warm-gray-300">
                  {currentItem?.clipName ?? 'Loading…'}
                </span>
                <span className="text-xs opacity-70">
                  Video not available yet. The asset may still be processing.
                </span>
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Controls */}
      <div className="px-6 pb-10">
        <div className="text-center mb-6">
          <p className="text-warm-gray-400 text-sm uppercase tracking-wider mb-1">
            {currentItem?.phase}
          </p>
          <div className="flex items-center justify-center gap-2 mt-3">
            <ProgressBar value={elapsed / itemDurationSec} className="w-48 opacity-70" />
            <span className="text-warm-gray-400 text-sm tabular-nums">
              {mins}:{String(secs).padStart(2, '0')} / {totalMins}:
              {String(totalSecs).padStart(2, '0')}
            </span>
          </div>
        </div>

        {/* Next phase preview */}
        {items[currentItemIndex + 1] && (
          <p className="text-center text-sm text-warm-gray-400 mb-4">
            Next:{' '}
            <span className="text-warm-gray-300">
              {items[currentItemIndex + 1]?.clipName ?? items[currentItemIndex + 1]?.phase}
            </span>
          </p>
        )}

        <div className="flex justify-center gap-4">
          <button
            type="button"
            onClick={handleTogglePlayback}
            className="px-6 py-3 rounded-xl text-sm text-warm-gray-300 border border-warm-gray-600 hover:border-warm-gray-400 transition-colors"
          >
            {isPlaying ? 'Pause' : 'Resume'}
          </button>
          <Button variant="sage" onClick={advanceOrComplete}>
            {currentItemIndex < items.length - 1 ? 'Next →' : 'Finish'}
          </Button>
        </div>
      </div>
    </div>
  )
}
